import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  memo,
} from "react";
// FCP/LCP 优化：直接 import 具体模块，避免走 src/index.ts / components barrel，
// 否则 main chunk（MainShell 引用 MusicContext）会拖入整个 utils + components 全家。
import utils from "../../utils/utils";
import PopupPlayer from "./PopupPlayer";
import { MusicItem, PlayMode } from "../../types/contexts";
import { useStartupDataFlattened } from "../../modules/startup/stores";
import { Popup } from "../../components/Base/Popup";
import { MusicContextRef } from "./context";

// 单独 re-export 方便业务侧保留 `from "@/contexts/MusicContext"` 的旧写法
export { useMusicContext } from "./context";

let musicInited = false;

export const MusicContextProvider = memo(function MusicContextProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const startupData = useStartupDataFlattened();
  const [playerShow, setPlayerShow] = useState(false);
  const [inited, setInited] = useState(false);
  const [musicList, setMusicList] = useState<MusicItem[]>([]);
  const [currentMusic, setCurrentMusic] = useState<MusicItem | undefined>();
  const [volume, setVolume] = useState(50);
  const [playing, setPlaying] = useState(false);
  const [playMode, setPlayMode] = useState<PlayMode>("list");
  const [currentMusicInstance, setCurrentMusicInstance] = useState<
    { currentTime: number } | undefined
  >();
  const audioRef = useRef<HTMLAudioElement>(new Audio());
  const [remoteList, setRemoteList] = useState<MusicItem[]>([]);

  const playlist = useMemo(() => {
    return musicList.filter(x => x.cached);
  }, [musicList]);

  const changePlayMode = useCallback(() => {
    setPlayMode(prev => {
      if (prev === "list") return "single";
      if (prev === "single") return "shuffle";
      return "list";
    });
  }, []);

  const play = useCallback(() => {
    audioRef.current?.play();
    setPlaying(true);
  }, []);

  const pause = useCallback(() => {
    audioRef.current?.pause();
    setPlaying(false);
  }, []);

  const prev = useCallback(() => {
    if (playlist.length === 1) {
      audioRef.current.currentTime = 0;
      play();
      return;
    }
    if (playMode === "shuffle") {
      const currentIndex = playlist.findIndex(x => x.id === currentMusic?.id);
      let index = currentIndex;
      while (index === currentIndex) {
        index = Math.floor(Math.random() * playlist.length);
      }
      playMusic(playlist[index]);
    } else {
      const index = playlist.findIndex(x => x.id === currentMusic?.id);
      if (index === 0) {
        playMusic(playlist[playlist.length - 1]);
      } else {
        playMusic(playlist[index - 1]);
      }
    }
  }, [playlist, playMode, currentMusic]);

  const next = useCallback(() => {
    if (playlist.length === 1) {
      audioRef.current.currentTime = 0;
      play();
      return;
    }
    const currentIndex = playlist.findIndex(x => x.id === currentMusic?.id);
    if (playMode === "shuffle") {
      let index = currentIndex;
      while (index === currentIndex) {
        index = Math.floor(Math.random() * playlist.length);
      }
      playMusic(playlist[index]);
    } else {
      if (currentIndex === playlist.length - 1) {
        playMusic(playlist[0]);
      } else {
        playMusic(playlist[currentIndex + 1]);
      }
    }
  }, [playlist, playMode, currentMusic]);

  const seek = useCallback((value: number) => {
    audioRef.current.currentTime = value;
  }, []);

  const playMusic = useCallback(
    async (music: MusicItem) => {
      if (!music.cached) await cacheMusic(music);
      seek(0);
      setCurrentMusic(music);
      setTimeout(() => {
        play();
      }, 150);
    },
    [seek, play]
  );

  const cacheMusic = useCallback(async (music: MusicItem) => {
    setMusicList(prev =>
      prev.map(item =>
        item.id === music.id ? { ...item, caching: true } : item
      )
    );

    const duration = await getDuration(music);

    setMusicList(prev =>
      prev.map(item =>
        item.id === music.id
          ? { ...item, duration, caching: false, cached: true }
          : item
      )
    );
  }, []);

  const getDuration = useCallback(async (music: MusicItem): Promise<number> => {
    return new Promise(resolve => {
      fetch(utils.getAudioUrl(music.audio))
        .then(res => res.blob())
        .then(blob => {
          const file = new File([blob], `test.wav`, {
            type: "audio/wav",
            lastModified: Date.now(),
          });

          const audio = new Audio();
          audio.src = URL.createObjectURL(file);

          audio.addEventListener("loadedmetadata", () => {
            resolve(audio.duration);
            URL.revokeObjectURL(audio.src); // 清理内存
          });
        })
        .catch(() => resolve(0)); // 错误处理
    });
  }, []);

  const onEnded = useCallback(() => {
    if (playMode === "single") return;
    next();
  }, [playMode, currentMusic, playlist]);

  const onTimeUpdate = useCallback(
    (event: React.SyntheticEvent<HTMLAudioElement>) => {
      const target = event.target as HTMLAudioElement;
      setCurrentMusicInstance(prevState => ({
        ...prevState,
        currentTime: target.currentTime,
      }));
    },
    []
  );

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = volume / 100;
    }
  }, [volume]);

  const initMusic = useCallback(async () => {
    utils.syncRunning("init-music-list", async () => {
      if (musicInited) {
        return;
      }
      const resp = await utils.getAsCache(
        "/audio_item/list?t=" + parseInt(new Date().getTime() / 3600000 + ""),
        startupData
      );
      if (resp.status === 0) {
        musicInited = true;
        setInited(true);
        setRemoteList(resp.list);
      }
    });
  }, [startupData]);

  useEffect(() => {
    if (remoteList && remoteList.length > 0) {
      const processMusicList = async () => {
        const processedList = await Promise.all(
          remoteList.map(async (x: MusicItem, i: number) => {
            if (i < 2) {
              const duration = await getDuration(x);
              return { ...x, duration, cached: true };
            }
            return x;
          })
        );
        setMusicList(processedList);
        setCurrentMusic(processedList[0]);
      };

      processMusicList();
    }
  }, [remoteList, getDuration]);

  const contextValue = useMemo(
    () => ({
      setPlayerShow,
      playlist,
      playing,
      setPlaying,
      play,
      pause,
      volume,
      setVolume,
      initMusic,
      inited,
      currentMusic,
      musicList,
      playMode,
      changePlayMode,
      prev,
      next,
      seek,
      currentMusicInstance,
      playMusic,
      cacheMusic,
      playerShow,
      remoteList,
    }),
    [
      setPlayerShow,
      playlist,
      playing,
      setPlaying,
      play,
      pause,
      volume,
      setVolume,
      initMusic,
      inited,
      currentMusic,
      musicList,
      playMode,
      changePlayMode,
      prev,
      next,
      seek,
      currentMusicInstance,
      playMusic,
      cacheMusic,
      playerShow,
      remoteList,
    ]
  );

  return (
    <MusicContextRef.Provider value={contextValue}>
      {children}
      <audio
        style={{ display: "none" }}
        ref={audioRef}
        preload=""
        src={currentMusic ? utils.getAudioUrl(currentMusic.audio) : ""}
        loop={playMode === "single"}
        onEnded={onEnded}
        onTimeUpdate={onTimeUpdate}
      />
      <Popup
        visible={playerShow}
        style={{ "--max-width": "var(--vw, 100vw)" } as React.CSSProperties}
        maskStyle={
          {
            backdropFilter:
              "var(--design-mask-filter, saturate(180%) blur(10px))",
          } as React.CSSProperties
        }
        onMaskClick={() => setPlayerShow(false)}
      >
        <PopupPlayer />
      </Popup>
    </MusicContextRef.Provider>
  );
});
